import type { AreaComponent } from '@repo/api-contracts';
import { useState } from 'react';
import { useStore } from 'zustand';
import { fullAreaName, outlineToSurfaceMm, useEditAreaDraftStore } from '@/entities/edit-area';
import { FACE_NAMES_KO } from './face-labels';

const NO_OUTLINE_REASON = '상품 추출하기로 윤곽을 먼저 잡아 주세요.';
const CYLINDER_REASON = '원통 상품은 전체 영역 지정을 지원하지 않습니다.';
const SELECT_SURFACE_REASON = '표면을 선택해 주세요.';

/** 도구 줄의 '전체 영역' — 선택한 평면 표면의 윤곽 전체를 자유 다각형 인쇄 영역으로 만든다(또는 이 면의 첫 다각형을 갱신한다). */
export interface FullAreaTool {
  readonly disabled: boolean;
  /** 꺼진 사유. 켜져 있으면 null. */
  readonly reason: string | null;
  /** 마지막 실행이 거부된 사유. 그 판정을 낸 표면·인쇄 영역이 그대로일 때만 보인다(다시 추출하거나 영역이 바뀌면 사라진다). */
  readonly error: string | null;
  readonly apply: () => void;
}

export const useFullAreaTool = (): FullAreaTool => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  // 거부 사유와, 그 사유를 계산한 입력(표면·인쇄 영역 목록). 입력이 바뀌면 지난 판정은 더 이상 유효하지 않다.
  const [failure, setFailure] = useState<{
    readonly message: string;
    readonly surface: unknown;
    readonly components: unknown;
  } | null>(null);
  const faceKey = state.selection.face;
  const face = state.faces[faceKey];
  const surface = face?.surfaces.find((item) => item.id === state.selection.surfaceId);
  const outline = surface?.type === 'flat' ? surface.outline : undefined;
  const outlined = surface?.type === 'flat' && outline ? { ...surface, outline } : null;
  const hasSurfaces = (face?.surfaces.length ?? 0) > 0;
  const reason = outlined
    ? null
    : surface?.type === 'cylinder'
      ? CYLINDER_REASON
      : !surface && hasSurfaces
        ? SELECT_SURFACE_REASON
        : NO_OUTLINE_REASON;
  const disabled = !outlined || state.locked || state.isUnsupported;

  const apply = (): void => {
    if (!outlined || !face) return;
    const label = FACE_NAMES_KO[faceKey];
    const placed = face.placements
      .map((placement) => state.components.find((item) => item.id === placement.componentId))
      .filter(
        (item): item is AreaComponent => item !== undefined && item.shape?.type === 'polygon',
      );
    const first = placed[0];
    const holeCount = first?.shape?.type === 'polygon' ? (first.shape.holes?.length ?? 0) : 0;
    // 갱신은 윤곽으로 외곽을 바꾸며 인쇄 제외 영역을 지운다 — 먼저 알리고 확인받는다.
    const holeNotice = holeCount > 0 ? ` 인쇄 제외 영역 ${holeCount}개도 함께 삭제됩니다.` : '';
    const target =
      first !== undefined &&
      window.confirm(
        `기존 ‘${first.name}’을 갱신하시겠습니까? 취소하면 새로 만듭니다.${holeNotice}`,
      )
        ? first
        : null;
    const name =
      target?.name ??
      fullAreaName(
        label,
        state.components.map((item) => item.name),
      );
    const ok = state.applyDrawnShape(faceKey, {
      componentId: target?.id ?? null,
      name,
      surfaceId: outlined.id,
      points: outlineToSurfaceMm(outlined),
    });
    const next = store.getState();
    if (!ok) {
      setFailure({
        message: next.commandError ?? '전체 영역을 지정하지 못했습니다.',
        surface,
        components: state.components,
      });
      return;
    }
    setFailure(null);
    const resultId = target?.id ?? next.components.find((item) => item.name === name)?.id;
    if (resultId) next.setSelection({ componentId: resultId });
  };

  const error =
    failure !== null && failure.surface === surface && failure.components === state.components
      ? failure.message
      : null;
  return { disabled, reason, error, apply };
};
